/**
 * Catálogo único dos perfis de usuário do portal e das capacidades de cada um
 * (ADR-053, spec `portal-perfis-designador-empresa`).
 *
 * BFF e SPA reexportam daqui: a lista de perfis, o mapeamento Cognito ↔ perfil
 * e a matriz de capacidades não podem divergir entre os dois lados.
 *
 * Não confundir com `types/roles.ts` (`UserRole`, valores Cognito legados).
 */

export const PORTAL_USER_ROLES = [
  'platform_admin',
  'operator',
  'client_admin',
  'client_viewer',
  'auditor',
  'developer',
  'designador_admin',
  'designador_viewer',
  'empresa_supervisor',
  'empresa_viewer',
] as const;

export type PortalUserRole = (typeof PORTAL_USER_ROLES)[number];

/** Perfis criados pela spec `portal-perfis-designador-empresa` (convivem com os legados). */
export const NEW_PORTAL_ROLES = [
  'designador_admin',
  'designador_viewer',
  'empresa_supervisor',
  'empresa_viewer',
] as const satisfies readonly PortalUserRole[];

/** Perfis que enxergam todas as empresas (ADR-048 Full_Scope_Role). */
export const FULL_SCOPE_PORTAL_ROLES = ['platform_admin', 'operator'] as const satisfies readonly PortalUserRole[];

export const PORTAL_USER_ROLE_LABELS: Record<PortalUserRole, string> = {
  platform_admin: 'Platform Admin',
  operator: 'Operador',
  client_admin: 'Admin do Cliente',
  client_viewer: 'Visualizador',
  auditor: 'Auditor',
  developer: 'Desenvolvedor',
  designador_admin: 'Designador Admin',
  designador_viewer: 'Designador Visualizador',
  empresa_supervisor: 'Empresa Supervisor',
  empresa_viewer: 'Empresa Visualizador',
};

/** Valor gravado em `custom:role` no Cognito para cada perfil (portal → Cognito). */
export const COGNITO_ROLE_BY_PORTAL_ROLE: Record<PortalUserRole, string> = {
  platform_admin: 'PLATFORM_ADMIN',
  operator: 'OPERATOR',
  client_admin: 'CLIENT_ADMIN',
  client_viewer: 'CLIENT_VIEWER',
  auditor: 'AUDITOR',
  developer: 'DEVELOPER',
  designador_admin: 'DESIGNADOR_ADMIN',
  designador_viewer: 'DESIGNADOR_VIEWER',
  empresa_supervisor: 'EMPRESA_SUPERVISOR',
  empresa_viewer: 'EMPRESA_VIEWER',
};

/** Valores Cognito legados aceitos na leitura (não são mais gravados). */
const LEGACY_COGNITO_ROLES: Record<string, PortalUserRole> = {
  PROVIDER_OPERATOR: 'operator',
  BRANCH_MANAGER: 'operator',
  SAFE_OPERATOR: 'client_viewer',
};

const PORTAL_ROLE_BY_COGNITO: Record<string, PortalUserRole> = Object.fromEntries(
  (Object.entries(COGNITO_ROLE_BY_PORTAL_ROLE) as Array<[PortalUserRole, string]>).map(([role, cognito]) => [cognito, role]),
);

export function isPortalUserRole(value: unknown): value is PortalUserRole {
  return typeof value === 'string' && (PORTAL_USER_ROLES as readonly string[]).includes(value);
}

export function isNewPortalRole(role: PortalUserRole): boolean {
  return (NEW_PORTAL_ROLES as readonly string[]).includes(role);
}

/**
 * Cognito `custom:role` → perfil do portal (sentido Cognito → portal).
 * Aceita o valor gravado (`DESIGNADOR_ADMIN`), os legados (`PROVIDER_OPERATOR`,
 * `BRANCH_MANAGER`, `SAFE_OPERATOR`) e o próprio id do perfil (`designador_admin`).
 * Retorna `null` para valor desconhecido (o chamador responde 401).
 */
export function portalRoleFromCognito(raw: string | null | undefined): PortalUserRole | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (value === '') return null;
  if (isPortalUserRole(value)) return value;
  const upper = value.toUpperCase();
  return PORTAL_ROLE_BY_COGNITO[upper] ?? LEGACY_COGNITO_ROLES[upper] ?? null;
}

// ─── Hierarquia de atribuição ────────────────────────────────────────────────

/** Nível de privilégio por perfil: quem atribui perfis só atribui do próprio nível para baixo. */
export const PORTAL_ROLE_LEVELS: Record<PortalUserRole, number> = {
  platform_admin: 100,
  operator: 80,
  client_admin: 60,
  designador_admin: 55,
  empresa_supervisor: 50,
  client_viewer: 40,
  designador_viewer: 38,
  empresa_viewer: 35,
  auditor: 30,
  developer: 20,
};

/**
 * `actor` pode atribuir `target`? Os perfis novos só pelo `platform_admin`
 * (spec portal-perfis-designador-empresa, Req 6.1); os demais pelo nível. A
 * regra explícita vem antes do nível (o `client_admin` tem nível 60).
 */
export function canAssignPortalRole(actor: PortalUserRole, target: PortalUserRole): boolean {
  if (isNewPortalRole(target)) return actor === 'platform_admin';
  return PORTAL_ROLE_LEVELS[actor] >= PORTAL_ROLE_LEVELS[target];
}

/** Perfis que `actor` pode atribuir, do maior para o menor nível. */
export function assignablePortalRoles(actor: PortalUserRole): PortalUserRole[] {
  return [...PORTAL_USER_ROLES]
    .sort((a, b) => PORTAL_ROLE_LEVELS[b] - PORTAL_ROLE_LEVELS[a])
    .filter((target) => canAssignPortalRole(actor, target));
}

// ─── Capacidades ─────────────────────────────────────────────────────────────

/** Flags de acesso por perfil, usadas pelo SPA (UI) e pelo BFF (API). */
export interface PortalCapabilities {
  /** Can view data across all tenants (platform_admin only) */
  canReadAllTenants: boolean;
  /** Can provision, decommission, and configure devices */
  canManageDevices: boolean;
  /** Can approve collection requests in the Four-Eyes workflow */
  canApproveCollections: boolean;
  /** Can create, rotate, and revoke API keys */
  canManageApiKeys: boolean;
  /** Can access the audit trail screen */
  canAccessAuditTrail: boolean;
  /** Can reprocess items from the Dead Letter Queue */
  canReprocessDLQ: boolean;
  /** Can invite, edit, and deactivate users within scope */
  canManageUsers: boolean;
  /** Can access the developer sandbox environment */
  canAccessSandbox: boolean;
  /** Can generate and download reports */
  canExportReports: boolean;
  /** Can view financial data (balances, collections, conciliação) */
  canViewFinancials: boolean;
  /** Can create and manage webhook subscriptions */
  canManageWebhooks: boolean;
  /** Can access the Help Desk */
  canAccessHelpDesk: boolean;
  /** Can open and reply to Help Desk requests as a client */
  canWriteHelpDesk: boolean;
  /** Can respond to Help Desk requests (operator/admin) and write internal notes */
  canRespondHelpDesk: boolean;
  /** Can access the Suporte Técnico screen (iClass integration) */
  canAccessSuporteTecnico: boolean;
  /** Can access the Estoque e Inventário screen */
  canAccessEstoque: boolean;
  /** Can configure white-label branding for tenants */
  canManageWhiteLabel: boolean;
  /** Can create, edit and deactivate empresas */
  canManageEmpresas: boolean;
  /** Can create, edit and deactivate filiais */
  canManageFiliais: boolean;
  /** Can manage equipment operators and their per-equipment permissions */
  canManageOperadores: boolean;
  /** Can edit the access grants of portal users */
  canManageUserGrants: boolean;
  /** Can see the Cadastro tab of /equipamentos */
  canViewEquipamentoCadastro: boolean;
  /** Can see the "Usuários do Portal" tab in Pessoas */
  canViewPortalUsers: boolean;
  /** Can acknowledge and resolve alerts (Requirement 10.4) */
  canManageAlerts: boolean;
}

/** Nenhuma capacidade (usuário não autenticado). */
export const NO_PORTAL_CAPABILITIES: PortalCapabilities = {
  canReadAllTenants: false,
  canManageDevices: false,
  canApproveCollections: false,
  canManageApiKeys: false,
  canAccessAuditTrail: false,
  canReprocessDLQ: false,
  canManageUsers: false,
  canAccessSandbox: false,
  canExportReports: false,
  canViewFinancials: false,
  canManageWebhooks: false,
  canAccessHelpDesk: false,
  canWriteHelpDesk: false,
  canRespondHelpDesk: false,
  canAccessSuporteTecnico: false,
  canAccessEstoque: false,
  canManageWhiteLabel: false,
  canManageEmpresas: false,
  canManageFiliais: false,
  canManageOperadores: false,
  canManageUserGrants: false,
  canViewEquipamentoCadastro: false,
  canViewPortalUsers: false,
  canManageAlerts: false,
};

/**
 * Matriz perfil → capacidades. Os 6 perfis legados reproduzem os valores de
 * antes desta spec; as flags novas seguem o design (§2, decisão D2).
 */
export const PORTAL_ROLE_CAPABILITIES: Record<PortalUserRole, PortalCapabilities> = {
  platform_admin: {
    canReadAllTenants: true,
    canManageDevices: true,
    canApproveCollections: true,
    canManageApiKeys: true,
    canAccessAuditTrail: true,
    canReprocessDLQ: true,
    canManageUsers: true,
    canAccessSandbox: true,
    canExportReports: true,
    canViewFinancials: true,
    canManageWebhooks: true,
    canAccessHelpDesk: true,
    canWriteHelpDesk: true,
    canRespondHelpDesk: true,
    canAccessSuporteTecnico: true,
    canAccessEstoque: true,
    canManageWhiteLabel: true,
    canManageEmpresas: true,
    canManageFiliais: true,
    canManageOperadores: true,
    canManageUserGrants: true,
    canViewEquipamentoCadastro: true,
    canViewPortalUsers: true,
    canManageAlerts: true,
  },
  operator: {
    ...NO_PORTAL_CAPABILITIES,
    canManageDevices: true,
    canApproveCollections: true,
    canReprocessDLQ: true,
    canManageUsers: true,
    canExportReports: true,
    canViewFinancials: true,
    canAccessHelpDesk: true,
    canWriteHelpDesk: true,
    canRespondHelpDesk: true,
    canAccessSuporteTecnico: true,
    canAccessEstoque: true,
    canManageEmpresas: true,
    canManageFiliais: true,
    canManageOperadores: true,
    canManageUserGrants: true,
    canViewEquipamentoCadastro: true,
    canViewPortalUsers: true,
    canManageAlerts: true,
  },
  client_admin: {
    ...NO_PORTAL_CAPABILITIES,
    canApproveCollections: true,
    canManageApiKeys: true,
    canManageUsers: true,
    canExportReports: true,
    canViewFinancials: true,
    canManageWebhooks: true,
    canAccessHelpDesk: true,
    canWriteHelpDesk: true,
    canManageUserGrants: true,
    canViewEquipamentoCadastro: true,
    canManageAlerts: true,
  },
  client_viewer: {
    ...NO_PORTAL_CAPABILITIES,
    canExportReports: true,
    canViewFinancials: true,
    canAccessHelpDesk: true,
    canWriteHelpDesk: true,
    canViewEquipamentoCadastro: true,
    canManageAlerts: true,
  },
  auditor: {
    ...NO_PORTAL_CAPABILITIES,
    canReadAllTenants: true,
    canAccessAuditTrail: true,
    canExportReports: true,
    canViewFinancials: true,
    canViewEquipamentoCadastro: true,
    canManageAlerts: true,
  },
  developer: {
    ...NO_PORTAL_CAPABILITIES,
    canManageApiKeys: true,
    canAccessSandbox: true,
    canManageWebhooks: true,
    canViewEquipamentoCadastro: true,
    canManageAlerts: true,
  },
  // Empresas e Filiais só leitura para os perfis novos (Requirement 12, emenda 2026-09-29).
  designador_admin: {
    ...NO_PORTAL_CAPABILITIES,
    canViewFinancials: true,
    canAccessHelpDesk: true,
    canWriteHelpDesk: true,
    canManageOperadores: true,
    canManageUserGrants: true,
    canViewPortalUsers: true,
  },
  designador_viewer: {
    ...NO_PORTAL_CAPABILITIES,
    canViewFinancials: true,
    canAccessHelpDesk: true,
    canViewPortalUsers: true,
  },
  empresa_supervisor: {
    ...NO_PORTAL_CAPABILITIES,
    canViewFinancials: true,
    canAccessHelpDesk: true,
    canWriteHelpDesk: true,
    canManageOperadores: true,
  },
  empresa_viewer: {
    ...NO_PORTAL_CAPABILITIES,
    canViewFinancials: true,
    canAccessHelpDesk: true,
  },
};

export function resolvePortalCapabilities(role: PortalUserRole): PortalCapabilities {
  return PORTAL_ROLE_CAPABILITIES[role];
}
